import { useEffect, useRef, useState } from "react";
import { buildJobEditDraft, buildJobEditPayload } from "./jobEdit";
import { WorkspaceButton } from "./ui";
import { WorkspaceDrawer } from "./WorkspaceDrawer";

/**
 * One labeled field in the edit panel.
 * @param {{ label: string, hint?: string, children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
function EditField({ label, hint, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[11px] font-normal leading-normal text-[#6e7b84]">{label}</span>
      {children}
      {hint ? (
        <span className="text-[11px] font-normal leading-normal text-[#909ba3]">{hint}</span>
      ) : null}
    </label>
  );
}

const fieldClass =
  "w-full rounded-[6px] border border-[#e2e7ea] px-3 text-[13px] font-medium text-[#202b33] outline-none focus:border-[#167366]";

/**
 * Edit the dispatch note, customer reference, commodity, and appointment without leaving the workspace.
 * @param {{
 *   open: boolean,
 *   job: object|null|undefined,
 *   saving?: boolean,
 *   onClose: () => void,
 *   onSave: (payload: object) => void | Promise<void>,
 * }} props
 * @returns {JSX.Element}
 */
export function JobEditPanel({ open, job, saving = false, onClose, onSave }) {
  const jobRef = useRef(job);
  jobRef.current = job;
  const [draft, setDraft] = useState(() => buildJobEditDraft(job));

  useEffect(() => {
    if (!open) return;
    setDraft(buildJobEditDraft(jobRef.current));
  }, [open]);

  /**
   * @param {React.FormEvent} event
   */
  async function handleSubmit(event) {
    event.preventDefault();
    const payload = buildJobEditPayload(draft, job);
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    await onSave(payload);
  }

  return (
    <WorkspaceDrawer
      open={open}
      title="Edit job"
      onClose={onClose}
      footer={
        <>
          <WorkspaceButton onClick={onClose} disabled={saving}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            tone="primary"
            type="submit"
            form="job-workspace-edit"
            disabled={saving}
          >
            {saving ? "Saving" : "Save changes"}
          </WorkspaceButton>
        </>
      }
    >
      <form id="job-workspace-edit" className="flex flex-col gap-5" onSubmit={handleSubmit}>
        <EditField label="Dispatch note">
          <textarea
            autoFocus
            rows={5}
            value={draft.note}
            onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))}
            className={`${fieldClass} py-2 leading-[1.6]`}
          />
        </EditField>
        <EditField label="Customer reference">
          <input
            value={draft.customerReference}
            onChange={(event) =>
              setDraft((current) => ({ ...current, customerReference: event.target.value }))
            }
            className={`${fieldClass} h-9`}
          />
        </EditField>
        <EditField label="Commodity">
          <input
            value={draft.commodity}
            onChange={(event) =>
              setDraft((current) => ({ ...current, commodity: event.target.value }))
            }
            className={`${fieldClass} h-9`}
          />
        </EditField>
        <EditField label="Appointment · UTC" hint="Enter the time in UTC.">
          <input
            type="datetime-local"
            value={draft.appointmentDate}
            onChange={(event) =>
              setDraft((current) => ({ ...current, appointmentDate: event.target.value }))
            }
            className={`${fieldClass} h-9`}
          />
        </EditField>
      </form>
    </WorkspaceDrawer>
  );
}
